import { runOperation } from '../host.js';
import { VaultStorage } from '../crypto/vault-storage.js';
import { uploadAttachment } from './attachment-service.js';
import { attachmentReferences, safeArchiveFilename } from '../../shared/utils/map-archive.js';
import { t } from '../../shared/i18n/index.js';

/** Upload fresh ciphertext/keys into a newly created map. Never delete a source. */
export async function restoreMapAttachments(archive, sourceDocId, targetDocId, data, { onProgress } = {}) {
  if (onProgress) return restoreMapAttachmentsImpl(archive, sourceDocId, targetDocId, data, onProgress);
  return runOperation(t('vault.restore.title'), progress => restoreMapAttachmentsImpl(archive, sourceDocId, targetDocId, data, progress.update),
    result => [t(result.warnings.length ? 'vault.restore.needsAttention' : 'vault.restore.done'), result.warnings.length > 0]);
}

async function restoreMapAttachmentsImpl(archive, sourceDocId, targetDocId, data, report) {
  report({ label: t('vault.restore.preparing') });
  const references = attachmentReferences(data);
  const selected = [...archive.attachments.values()].filter(item =>
    item.usedBy.includes(sourceDocId) || references.has(item.ref));
  const replacements = new Map(), warnings = [];
  if (!selected.length) return { replacements, warnings };
  // A map on this device keeps its attachments on the device, under its own
  // key (uploadAttachment routes there). An online map was registered with
  // its key when it was created (VaultStorage.createDoc).
  let key, bytes;
  try {
    bytes = await VaultStorage.getAttachmentKeyBytes(targetDocId, { create: true });
    key = await crypto.subtle.importKey('raw', bytes, 'AES-GCM', false, ['encrypt', 'decrypt']);
  } catch (error) {
    return { replacements, warnings: [t('vault.restore.failed', { error: error.message })] };
  } finally { bytes?.fill(0); }
  let processed = 0;
  for (const item of selected) {
    processed++;
    try {
      const file = new File([item.bytes], safeArchiveFilename(item.filename), { type: item.mimeType });
      const { attachmentId } = await uploadAttachment(targetDocId, file, key, { onProgress: event =>
        report({ ...event, label: t('vault.archive.fileOf', { label: event.label, current: processed, total: selected.length }) }) });
      replacements.set(item.ref, `attachment:${targetDocId}/${attachmentId}`);
    } catch (error) { warnings.push(t('vault.restore.fileFailed', { name: item.filename, error: error.message })); }
  }
  return { replacements, warnings };
}

/** Keep exported files that had no note reference visible and immune to the
 * normal unused-attachment sweep after restore. */
export function restoredAttachmentNote(archive, sourceDocId, data, replacements) {
  const referenced = attachmentReferences(data);
  const files = [...archive.attachments.values()].filter(item => item.usedBy.includes(sourceDocId)
    && replacements.has(item.ref) && !referenced.has(item.ref));
  if (!files.length) return null;
  const label = name => String(name).replace(/[\[\]\\\r\n]/g, '_');
  return { id: crypto.randomUUID(), title: t('vault.restore.noteTitle'), created: Date.now(), updated: Date.now(),
    content: files.map(item => `[${label(item.filename)}](${replacements.get(item.ref)})`).join('\n\n'), refnotes: [] };
}

/** Archive bytes are in-memory import context, never serialized back into JSON. */
export function attachArchiveContext(data, archive) {
  if (archive) Object.defineProperty(data, '_attachmentArchive', { value: archive, configurable: true });
  return data;
}
